import React, { useState, useEffect } from 'react';
import { filesService } from 'api/files.service';
import './FileUpload.css';
import AttachmentIcon from '@mui/icons-material/Attachment';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import UploadIcon from '@mui/icons-material/Upload';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { CircularProgress, Tooltip } from '@mui/material';
import { IfileValue } from 'shared/interfaces/formApplication';
import { NotificationSnackbar } from '../NotificationSnackbar';
import { getOtpToken } from 'api/axiosApiProvider';

// Use the shared interface for consistency
interface fileValue extends IfileValue {}

interface FileUploadsProps {
    title?: string;
    files?: fileValue[];
    fieldIndex?: number;
    updateSelectedFiles?: (fieldIdex: number, fieldValue: string | number | boolean | fileValue[]) => void;
    applicationId?: string;
    applicationCategory?: string;
    formId?: string;
    disabled?: boolean;
}

// Updated file validation configuration matching backend
const ALLOWED_FILE_TYPES = {
    'application/pdf': '.pdf',
    'application/msword': '.doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
    'application/vnd.ms-excel': '.xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
    'text/plain': '.txt',
    'image/jpeg': '.jpg',
    'image/png': '.png',
    'image/gif': '.gif',
};

const FILE_CONFIG = {
    maxFileSize: 5 * 1024 * 1024, // 5MB in bytes
    maxFiles: 5,
    allowedExtensions: Object.values(ALLOWED_FILE_TYPES),
};

interface NotificationState {
    open: boolean;
    message: string;
    severity: 'error' | 'warning' | 'info' | 'success';
}

const FileUpload: React.FC<FileUploadsProps> = ({
    files,
    fieldIndex,
    updateSelectedFiles,
    applicationId,
    applicationCategory,
    formId,
    disabled,
}) => {
    const [selectedFiles, setSelectedFiles] = useState<fileValue[]>(files || []);

    // Keep the list in sync when the parent passes new files — e.g. after submit the server
    // returns the files moved to permanent storage (new fileIds); without this the component
    // kept the temporary ids and the download failed. Same as Internal's ApplicationFormFileUpload.
    useEffect(() => {
        if (files && files.length >= 0) {
            setSelectedFiles(files);
        }
    }, [files]);
    const [uploading, setUploading] = useState<boolean>(false);
    const [notification, setNotification] = useState<NotificationState>({
        open: false,
        message: '',
        severity: 'info',
    });

    const showNotification = (message: string, severity: 'error' | 'warning' | 'info' | 'success') => {
        setNotification({ open: true, message, severity });
    };

    const closeNotification = () => {
        setNotification((prev) => ({ ...prev, open: false }));
    };

    // Check for expired temporary files periodically
    useEffect(() => {
        const checkExpiredFiles = () => {
            const now = new Date();
            const validFiles = selectedFiles.filter((file) => {
                // If file is temporary and has expiresAt, check if it's still valid
                if (file.isTemporary && file.expiresAt) {
                    const expirationDate = new Date(file.expiresAt);
                    const isValid = expirationDate > now;

                    if (!isValid) {
                        console.warn(`File ${file.fileName} has expired and will be removed`);
                    }

                    return isValid;
                }
                // Non-temporary files or files without expiresAt are always valid
                return true;
            });

            // If some files were filtered out (expired), update the state
            if (validFiles.length !== selectedFiles.length) {
                setSelectedFiles(validFiles);
                if (updateSelectedFiles && fieldIndex !== undefined) {
                    updateSelectedFiles(fieldIndex, validFiles);
                }
                showNotification('קבצים זמניים פג תוקפם והוסרו', 'warning');
            }
        };

        // Check immediately on mount and when files change
        checkExpiredFiles();

        // Then check every minute
        const interval = setInterval(checkExpiredFiles, 60000);

        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFiles.length]); // Only re-run when number of files changes, not the files themselves

    // File validation function
    const validateFile = (file: File): { isValid: boolean; errorMessage?: string } => {
        // Check file size
        if (file.size > FILE_CONFIG.maxFileSize) {
            return {
                isValid: false,
                errorMessage: `הקובץ ${file.name} גדול מדי. גודל מקסימלי מותר: 5MB`,
            };
        }

        // Check file type
        const isValidType =
            Object.keys(ALLOWED_FILE_TYPES).includes(file.type) ||
            FILE_CONFIG.allowedExtensions.some((ext) => file.name.toLowerCase().endsWith(ext));

        if (!isValidType) {
            return {
                isValid: false,
                errorMessage: `סוג הקובץ ${file.name} אינו נתמך. קבצים מותרים: PDF, DOC, DOCX, XLS, XLSX, TXT, JPG, PNG, GIF`,
            };
        }

        return { isValid: true };
    };

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            const newFiles = Array.from(event.target.files);
            const totalFiles = selectedFiles.length + newFiles.length;

            // Check total file count
            if (totalFiles > FILE_CONFIG.maxFiles) {
                showNotification(`ניתן להעלות עד ${FILE_CONFIG.maxFiles} קבצים בלבד.`, 'error');
                return;
            }

            // Validate each file
            for (const file of newFiles) {
                const validation = validateFile(file);
                if (!validation.isValid) {
                    showNotification(validation.errorMessage!, 'error');
                    return;
                }
            }

            // Calculate total size including existing files
            const existingFilesSize = selectedFiles.reduce((sum, file) => sum + file.fileSize, 0);
            const newFilesSize = newFiles.reduce((sum, file) => sum + file.size, 0);
            const totalSize = existingFilesSize + newFilesSize;

            if (totalSize > FILE_CONFIG.maxFileSize * FILE_CONFIG.maxFiles) {
                showNotification(`הגודל הכולל של הקבצים גדול מדי. מקסימום ${FILE_CONFIG.maxFiles * 5}MB.`, 'error');
                return;
            }

            setUploading(true);

            try {
                // Upload each file individually using the new temporary file system
                const uploadResults: fileValue[] = [];

                for (const file of newFiles) {
                    try {
                        console.log('📤 FileUpload - Preparing to upload file:', {
                            fileName: file.name,
                            applicationId,
                            fieldIndex,
                            applicationCategory,
                            formId,
                            attachmentType: 'field-attachment',
                        });

                        const response = await filesService.uploadTemporary(
                            file,
                            applicationId,
                            fieldIndex,
                            applicationCategory,
                            formId,
                            'field-attachment',
                        );

                        if (response.success && response.file) {
                            const fileValue: fileValue = {
                                fileId: response.file.fileId,
                                fileName: file.name, // Use original filename
                                fileSize: response.file.fileSize,
                                fileExtension: response.file.fileExtension,
                                fileUrl: filesService.getFileViewUrl(response.file.fileId),
                                isTemporary: response.file.isTemporary,
                                expiresAt: response.file.expiresAt ? new Date(response.file.expiresAt) : undefined,
                                uploadedAt: response.file.uploadedAt ? new Date(response.file.uploadedAt) : undefined,
                            };
                            uploadResults.push(fileValue);
                        } else {
                            throw new Error(response.error?.message || 'Upload failed');
                        }
                    } catch (fileError) {
                        console.error(`Error uploading file ${file.name}:`, fileError);
                        showNotification(`שגיאה בהעלאת הקובץ ${file.name}: ${(fileError as Error).message}`, 'error');
                    }
                }

                if (uploadResults.length > 0) {
                    const newFilesArray = [...selectedFiles, ...uploadResults];
                    setSelectedFiles(newFilesArray);
                    updateSelectedFiles?.(fieldIndex, newFilesArray);

                    showNotification(`${uploadResults.length} קבצים הועלו בהצלחה!`, 'success');
                }
            } catch (error) {
                console.error('Upload error:', error);
                showNotification('שגיאה כללית בהעלאת הקבצים.', 'error');
            } finally {
                setUploading(false);
                // Reset file input
                event.target.value = '';
            }
        }
    };

    const handleFileRemove = async (fileId: string) => {
        try {
            const response = await filesService.removeTemporary(fileId, applicationId);

            if (response.success) {
                const newFilteredFilesArray = selectedFiles.filter((file) => file.fileId !== fileId);
                setSelectedFiles(newFilteredFilesArray);
                updateSelectedFiles?.(fieldIndex, newFilteredFilesArray);
                showNotification(response.message || 'הקובץ נמחק בהצלחה', 'success');
            } else {
                throw new Error(response.error?.message || 'Failed to delete file');
            }
        } catch (error) {
            console.error('Delete error:', error);
            showNotification(`שגיאה במחיקת הקובץ: ${(error as Error).message}`, 'error');
        }
    };

    const handleFileDownload = async (fileId: string, fileName: string) => {
        try {
            // Find the file in our selected files list
            const file = selectedFiles.find((f) => f.fileId === fileId);

            console.log('⬇️ FileUpload - handleFileDownload debug:', {
                requestedFileId: fileId,
                requestedFileName: fileName,
                foundFile: file,
            });

            let fileUrl: string;
            if (file && file.fileUrl) {
                // Use existing file URL (works for both temporary and permanent)
                fileUrl = file.fileUrl;
                console.log('📤 Using existing file URL:', fileUrl);
            } else {
                // Fallback: build view URL from fileId
                fileUrl = filesService.getFileViewUrl(fileId);
                console.log('📤 Using fileId-based URL:', fileUrl);
            }

            // Fetch the file to get it as a blob (to preserve filename with extension)
            // Include OTP session token for access control
            console.log('📥 Fetching file for download...');
            const otpToken = applicationId ? getOtpToken(applicationId) : null;
            const fetchHeaders: HeadersInit = {};
            if (otpToken) {
                fetchHeaders['Authorization'] = `Bearer ${otpToken}`;
            }
            const response = await fetch(fileUrl, { headers: fetchHeaders });

            if (!response.ok) {
                throw new Error(`Failed to download file: ${response.statusText}`);
            }

            // Convert response to blob
            const blob = await response.blob();
            console.log('✅ File fetched as blob:', blob.type, blob.size);

            // Create a blob URL (same-origin, so download attribute will work properly)
            const blobUrl = URL.createObjectURL(blob);

            // Create temporary <a> element for download
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileName; // Now this will work correctly with the file extension
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Cleanup: revoke the blob URL to free memory
            setTimeout(() => URL.revokeObjectURL(blobUrl), 100);

            console.log('✅ File download initiated:', fileName);
        } catch (error) {
            console.error('Download error:', error);
            showNotification(`שגיאה בהורדת הקובץ: ${(error as Error).message}`, 'error');
        }
    };

    // const handleFileView = async (fileId: string) => {
    //     try {
    //         // Find the file in our selected files list
    //         const file = selectedFiles.find((f) => f.fileId === fileId);
    //
    //         console.log('🔍 FileUpload - handleFileView debug:', {
    //             requestedFileId: fileId,
    //             foundFile: file,
    //             selectedFiles: selectedFiles,
    //             allFileIds: selectedFiles.map((f) => f.fileId),
    //             allFileUrls: selectedFiles.map((f) => ({ id: f.fileId, url: f.fileUrl })),
    //         });
    //
    //         let fileUrl: string;
    //         if (file && file.fileUrl) {
    //             // Use existing file URL (works for both temporary and permanent)
    //             fileUrl = file.fileUrl;
    //             console.log('📤 Using existing file URL:', fileUrl);
    //         } else {
    //             // Fallback: build view URL from fileId
    //             fileUrl = filesService.getFileViewUrl(fileId);
    //             console.log('📤 Using fileId-based URL:', fileUrl);
    //         }
    //
    //         window.open(fileUrl, '_blank');
    //     } catch (error) {
    //         console.error('View error:', error);
    //         showNotification(`שגיאה בצפייה בקובץ: ${(error as Error).message}`, 'error');
    //     }
    // };

    return (
        <div style={{ width: 'fit-content', maxWidth: '100%' /* #261: cap at the field width on narrow screens */ }}>
            {selectedFiles.length > 0 && (
                <ul style={{ marginRight: '5px' }}>
                    {selectedFiles.map((file, index) => {
                        // Check if file is temporary and expiring soon (within 24 hours)
                        const isTemporary = file.isTemporary === true;
                        const isExpiringSoon =
                            isTemporary &&
                            file.expiresAt &&
                            new Date(file.expiresAt).getTime() - Date.now() < 24 * 60 * 60 * 1000;

                        // Format expiration date for tooltip
                        const expirationDate = file.expiresAt
                            ? new Date(file.expiresAt).toLocaleDateString('he-IL', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                              })
                            : '';

                        return (
                            <li key={index} className="file-item">
                                <AttachmentIcon style={{ color: 'rgb(107, 107, 107)' }} />
                                <span style={{ flex: 1 }}>{file.fileName}</span>

                                {/* Temporary file indicator */}
                                {isTemporary && (
                                    <Tooltip
                                        title={`קובץ זמני - יוסר אוטומטית ב-${expirationDate} אם הטופס לא יוגש. הגש את הטופס כדי לשמור לצמיתות.`}
                                        arrow
                                        placement="top"
                                    >
                                        <AccessTimeIcon
                                            style={{
                                                fontSize: '18px',
                                                color: isExpiringSoon ? '#ff9800' : '#9e9e9e',
                                                marginLeft: '8px',
                                                cursor: 'help',
                                            }}
                                        />
                                    </Tooltip>
                                )}

                                <button
                                    type="button"
                                    className="view-button"
                                    onClick={() => handleFileDownload(file.fileId, file.fileName)}
                                >
                                    <DownloadIcon style={{ fontSize: '15px', marginBottom: '1.3px' }} />
                                    הורד קובץ
                                </button>
                                {/* Old view button - kept for reference */}
                                {/* <button type="button" className="view-button" onClick={() => handleFileView(file.fileId)}>
                                    <VisibilityIcon style={{ fontSize: '15px', marginBottom: '1.3px' }} />
                                    צפה בקובץ
                                </button> */}
                                {!disabled && (
                                    <button type="button" className="remove-button" onClick={() => handleFileRemove(file.fileId)}>
                                        <CloseIcon style={{ fontSize: '15px', marginBottom: '1.3px' }} />
                                        הסר
                                    </button>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
            {/* Hidden file input, to enable custom behavior */}
            {!disabled && (
                <>
                    <input
                        type="file"
                        onChange={handleFileSelect}
                        multiple
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif"
                        style={{ display: 'none' }}
                        id="file-upload-input"
                        disabled={uploading}
                    />
                    {/* Custom elements to trigger functionality */}

                    <label
                        htmlFor="file-upload-input"
                        className={`upload-button ${uploading ? 'uploading' : ''}`}
                        style={{
                            opacity: uploading ? 0.6 : 1,
                            cursor: uploading ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                        }}
                    >
                        {uploading ? <CircularProgress size={16} color="primary" /> : <UploadIcon style={{ fontSize: '16px' }} />}
                        <span>{uploading ? 'מעלה קבצים...' : 'העלה קבצים מהמחשב'}</span>
                    </label>
                </>
            )}

            <NotificationSnackbar
                open={notification.open}
                message={notification.message}
                severity={notification.severity}
                onClose={closeNotification}
            />
        </div>
    );
};

export default FileUpload;
